import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Star, Navigation, Loader2, X, Users, Phone, Upload, UserPlus, Clock, Trophy, Share2 } from 'lucide-react';
import {
  ArrowLeft, Flag
} from 'lucide-react';
import Layout from '../components/Layout';
import Avatar from '../components/Avatar';
import { gamesApi, betsApi, coursesApi, communityApi, Course } from '../api';
import ContactService from '../services/ContactService';
import { sastTeeTime, formatSAST, parseSAST } from '../utils/datetime';
import GolfIcon from '../components/GolfIcon';
import '../styles/clubhouse.css';
import '../styles/quick-game.css';
import { trackEvent } from '../utils/analytics';
import { formatHandicapIndex } from '../utils/handicapIndex';

const FUNNY_GAME_NAMES = [
  'The No Mulligans Open', 'Fourball of Fury', 'The Hackers Championship',
  'Grip It and Braai It', 'The Boet Betterball', 'Lekker Lies Invitational',
  'Biltong and Birdies', 'Boerewors Betterball', 'The Back Nine Braai',
  'The Halfway House Hustle', 'Pie, Chips and Three Putts', 'The Windhoek Wedge-Off',
  'Castle Lite Cup', 'The Springbok Slice', 'The Protea Punch-Out',
  'Highveld Hookers', 'Cape Doctor Classic', 'Joburg Jolly Boys Open',
  'Durban Heat and Three Putts', 'The Karoo Carry', 'The Vaal Dam Slam',
  'Tshwane Tee Party', 'The Garden Route Shank', 'KZN Lost Ball Society',
  'The Nineteenth Hole National', 'Fairways and Load Shedding', 'Stage Six Stableford',
  'Eskom Ate My Back Nine', 'No Power, Plenty Slice', 'The R150 Kev Chase',
  'Where Is Kev Classic', 'Kev Left the Group', 'I’ll EFT You Open',
  'No Cash on Me Cup', 'Banking App Refresh Classic', 'The R5,000 Vanishing Act',
  'Pay Up, Play On', 'The Wallet Inspector', 'Friendly Stakes, Hostile Putts',
  'Gimmes Are for Cowards', 'That Was Definitely a Five', 'Put Me Down for Bogey',
  'The Breakfast Ball Invitational', 'Lost Ball Legal Defence Fund', 'Provisional? What Provisional?',
  'Trees Are Ninety Percent Air', 'The Cart Path Bounce Open', 'Club Selection Was Fine',
  'Blame the Caddie Classic', 'The Handicap Appreciation Day', 'Sandbaggers Anonymous',
  'Scratch Golfer Support Group', 'Weekend Warriors Cup', 'The Usual Suspects Open',
  'One Good Hole Championship', 'Fore Right Festival', 'The Double Bogey Derby',
  'Par Is Merely a Suggestion', 'The Unplayable Lie Society', 'Driver Off Every Tee',
  'The Three-Putt Tax', 'Nearest the Cooler Box', 'Longest Drive Home',
  'The Beer Money Major', 'Dop and Drop Invitational', 'The Sunday Scaries Stableford',
  'Whose Round Is It Anyway?', 'The Chairman’s Suspicious 79', 'Twenty-Four Handicap Heroics',
  'The Scorecard Audit', 'No Photos on the Downswing', 'The WhatsApp Group Major',
];

const QuickGame: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scoringFormat, setScoringFormat] = useState('stableford');
  const [holes, setHoles] = useState(18);
  const [playerEmails, setPlayerEmails] = useState('');
  const [betAmount, setBetAmount] = useState(50);
  const [customBet, setCustomBet] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Auto-scroll target: jump here once a tee time is chosen.
  const scoringRef = useRef<HTMLDivElement>(null);

  // Enhanced search states
  const [courseQuery, setCourseQuery] = useState('');
  const [courseSuggestions, setCourseSuggestions] = useState<Course[]>([]);
  const [nearbyCourses, setNearbyCourses] = useState<Course[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [showCourseSuggestions, setShowCourseSuggestions] = useState(false);
  const [isCourseLoading, setIsCourseLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationError, setLocationError] = useState('');

  // Friend search states
  const [friendQuery, setFriendQuery] = useState('');
  const [friendSuggestions, setFriendSuggestions] = useState<any[]>([]);
  const [selectedFriends, setSelectedFriends] = useState<any[]>([]);
  const [showFriendSuggestions, setShowFriendSuggestions] = useState(false);
  const [isFriendLoading, setIsFriendLoading] = useState(false);
  const [showContactUpload, setShowContactUpload] = useState(false);

  // Contact permission states
  const [contactPermission, setContactPermission] = useState<'granted' | 'denied' | 'prompt' | 'not_available'>('prompt');
  const [isContactLoading, setIsContactLoading] = useState(false);

  // Enhanced contact management states
  const [allContacts, setAllContacts] = useState<any[]>([]);
  const [contactFilters, setContactFilters] = useState({
    source: [] as ('api' | 'file' | 'manual')[],
    hasEmail: false,
    hasPhone: false,
    favorite: false
  });
  const [showContactFilters, setShowContactFilters] = useState(false);

  const recentCourses = [
    'Randpark Golf Club',
    'Glendower Golf Club', 
    'Royal Johannesburg & Kensington',
    'Houghton Golf Club'
  ];





  useEffect(() => {
    setScheduledDate(sastTeeTime('tomorrow'));
    
    // Pick a funny random game name
    const randomName = FUNNY_GAME_NAMES[Math.floor(Math.random() * FUNNY_GAME_NAMES.length)];
    setName(randomName);
    
    // Location is requested only when the golfer chooses Nearby.

    // Check contact permission
    checkContactPermission();
  }, []);

  
  // Resolve the phone's current GPS position and return the fresh coords.
  // Rejects on failure so callers can decide what to do (rather than silently
  // querying with a stale/default location, which produced wrong results).
  const getUserLocation = (): Promise<{ lat: number; lng: number }> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setLocationError('GPS is not supported by this device/browser');
        reject(new Error('no geolocation'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(coords);
          setLocationError('');
          resolve(coords);
        },
        (error) => {
          let errorMessage = 'Unable to get your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Enable location for nearby courses.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable right now.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          setLocationError(errorMessage);
          reject(error);
        },
        {
          // Force a brand-new high-accuracy GPS fix every time - never a
          // cached or coarse network/IP location (maximumAge: 0). Allow a
          // longer wait since a cold GPS fix can take a few seconds.
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0,
        }
      );
    });
  };

  // Fetch nearby courses using explicit coords (avoids reading stale state).
  const searchNearby = async (coords?: { lat: number; lng: number }, openDropdown = true) => {
    setIsCourseLoading(true);
    if (openDropdown) setShowCourseSuggestions(true);
    try {
      if (coords) {
        const nearby = await coursesApi.getNearbyCourses(coords.lat, coords.lng, 50, 10);
        setNearbyCourses(nearby);
      } else {
        const courses = await coursesApi.getCourses({ limit: 10 });
        setNearbyCourses(courses);
      }
    } catch (error) {
      try {
        const courses = await coursesApi.getCourses({ limit: 10 });
        setNearbyCourses(courses);
      } catch {
        setNearbyCourses([]);
      }
    } finally {
      setIsCourseLoading(false);
    }
  };

  // "Search Near Me": get a fresh GPS fix, then query with those exact coords.
  const handleSearchNearMe = async () => {
    setIsCourseLoading(true);
    setShowCourseSuggestions(true);
    try {
      const coords = await getUserLocation();
      await searchNearby(coords);
    } catch {
      // GPS denied/failed - show featured courses so the list isn't empty.
      await searchNearby(undefined);
    }
  };

  const getUserFallbackLocation = () => {
    // Try to determine location from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    if (timezone.includes('Johannesburg') || timezone.includes('Pretoria') || timezone.includes('Africa/Johannesburg')) {
      return { lat: -26.2041, lng: 28.0473 }; // Johannesburg
    } else if (timezone.includes('Cape Town') || timezone.includes('Africa/Cape_Town')) {
      return { lat: -33.9249, lng: 18.4241 }; // Cape Town
    } else if (timezone.includes('Durban') || timezone.includes('Africa/Johannesburg')) {
      return { lat: -29.8542, lng: 31.0218 }; // Durban
    } else {
      return { lat: -26.2041, lng: 28.0473 }; // Default to Johannesburg
    }
  };

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Contact permission functions
  const checkContactPermission = async () => {
    try {
      const permission = await ContactService.checkPermission();
      setContactPermission(permission.status);
    } catch (error) {
      console.error('Error checking contact permission:', error);
      setContactPermission('not_available');
    }
  };

  // Get browser compatibility info
  const getBrowserInfo = () => {
    return ContactService.getBrowserCompatibility();
  };

  const requestContactPermission = async () => {
    setIsContactLoading(true);
    try {
      const permission = await ContactService.requestPermission();
      setContactPermission(permission.status);
      
      if (permission.granted) {
        // Load contacts after permission is granted
        await loadContacts();
      }
    } catch (error) {
      console.error('Error requesting contact permission:', error);
      setContactPermission('denied');
    } finally {
      setIsContactLoading(false);
    }
  };

  const loadContacts = async () => {
    setIsFriendLoading(true);
    try {
      const contacts = await ContactService.getContacts();
      // Convert contacts to friend format
      const formattedContacts = contacts.map(contact => ({
        id: contact.id,
        name: contact.name.split(' ')[0] || contact.name,
        surname: contact.name.split(' ').slice(1).join(' ') || '',
        email: contact.email,
        phone: contact.phone,
        handicap: undefined,
        homeClub: undefined
      }));
      setFriendSuggestions(formattedContacts);
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setIsFriendLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsFriendLoading(true);
    try {
      // Get existing contacts for deduplication
      const existingContacts = friendSuggestions.map(friend => ({
        id: friend.id,
        name: `${friend.name} ${friend.surname}`.trim(),
        email: friend.email,
        phone: friend.phone,
        source: 'manual' as const,
        createdAt: new Date().toISOString(),
        groups: [],
        favorite: false
      }));

      // Use enhanced import with validation and deduplication
      const importResult = await ContactService.importContactsFromFileEnhanced(file, existingContacts);
      
      // Show import results
      if (importResult.errors.length > 0) {
        console.warn('Import errors:', importResult.errors);
      }

      // Format successful contacts
      const formattedContacts = importResult.contacts.map(contact => ({
        id: contact.id,
        name: contact.name.split(' ')[0] || contact.name,
        surname: contact.name.split(' ').slice(1).join(' ') || '',
        email: contact.email,
        phone: contact.phone,
        handicap: undefined,
        homeClub: undefined,
        source: contact.source,
        groups: contact.groups,
        favorite: contact.favorite
      }));

      // Show results to user
      if (importResult.contacts.length > 0) {
        setFriendSuggestions(prev => [...prev, ...formattedContacts]);
        setAllContacts(prev => [...prev, ...formattedContacts]);
        setShowContactUpload(false);
        
        // Show success message with details
        const message = `Successfully imported ${importResult.contacts.length} contacts!${
          importResult.duplicates.length > 0 ? ` (${importResult.duplicates.length} duplicates skipped)` : ''
        }${
          importResult.errors.length > 0 ? ` (${importResult.errors.length} errors)` : ''
        }`;
        
        // You could show this in a toast/notification instead of alert
        console.log(message);
      } else {
        // Show error if no contacts were imported
        const errorMessage = importResult.errors.length > 0 
          ? `Import failed: ${importResult.errors.slice(0, 3).join(', ')}`
          : 'No valid contacts found in file';
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error importing contacts:', error);
      alert('Failed to import contacts. Please check the file format and try again.');
    } finally {
      setIsFriendLoading(false);
    }
  };

  const searchCourses = async (query: string) => {
    if (!query.trim()) {
      setCourseSuggestions([]);
      setShowCourseSuggestions(false);
      return;
    }

    setIsCourseLoading(true);
    setShowCourseSuggestions(true);

    try {
      const results = await coursesApi.getCourses({
        search: query,
        limit: 20
      });
      setCourseSuggestions(results);
    } catch (error) {
      console.error('Error searching courses:', error);
      setCourseSuggestions([]);
    } finally {
      setIsCourseLoading(false);
    }
  };

  
  const handleCourseSelect = (course: Course) => {
    setCourseName(course.name);
    setSelectedCourse(course);
    setCourseQuery(course.name);
    setShowCourseSuggestions(false);
  };

  // Let players use ANY course name they typed, even if it isn't in our
  // database (e.g. Dainfern). No more dead-ends when a course isn't listed.
  const useTypedCourse = () => {
    const val = courseQuery.trim();
    if (!val) return;
    setCourseName(val);
    setSelectedCourse(null);
    setShowCourseSuggestions(false);
  };

  const handleFriendSelect = (friend: any) => {
    if (selectedFriends.some((f) => f.id === friend.id)) return;
    setSelectedFriends([...selectedFriends, friend]);
    setFriendQuery('');
    setFriendSuggestions([]);
    setShowFriendSuggestions(false);
  };

  const handleRemoveFriend = (friendId: string) => {
    setSelectedFriends(selectedFriends.filter((f) => f.id !== friendId));
  };

  const randomizeGameName = () => {
    const randomName = FUNNY_GAME_NAMES[Math.floor(Math.random() * FUNNY_GAME_NAMES.length)];
    setName(randomName);
  };

  // Search REGISTERED users (by name or email) so you can invite real
  // players, plus any locally imported contacts. Debounced.
  const friendSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchFriends = (query: string) => {
    setFriendQuery(query);
    const q = query.trim();

    if (friendSearchTimer.current) clearTimeout(friendSearchTimer.current);

    if (q.length < 2) {
      setFriendSuggestions([]);
      setShowFriendSuggestions(false);
      return;
    }

    setIsFriendLoading(true);
    setShowFriendSuggestions(true);

    friendSearchTimer.current = setTimeout(async () => {
      try {
        const users = await communityApi.searchUsers(q);
        const fromUsers = users.map((u) => {
          const parts = (u.full_name || '').trim().split(' ');
          return {
            id: `u${u.id}`,
            userId: u.id,
            name: parts[0] || u.full_name,
            surname: parts.slice(1).join(' '),
            handicap: u.handicap,
            homeClub: u.home_club,
            avatar_url: u.avatar_url,
          };
        });
        // Merge in any imported contacts that match (they carry emails).
        const fromContacts = ContactService.searchContactsEnhanced(q, allContacts, contactFilters);
        const merged = [...fromUsers, ...fromContacts].filter(
          (f) => !selectedFriends.some((s) => s.id === f.id)
        );
        setFriendSuggestions(merged);
      } catch (err) {
        setFriendSuggestions([]);
      } finally {
        setIsFriendLoading(false);
      }
    }, 300);
  };

  // Toggle contact filter
  const toggleContactFilter = (filterType: keyof typeof contactFilters, value: any) => {
    setContactFilters(prev => ({
      ...prev,
      [filterType]: filterType === 'source' 
        ? value 
        : !prev[filterType]
    }));
  };

  // Clear all filters
  const clearContactFilters = () => {
    setContactFilters({
      source: [],
      hasEmail: false,
      hasPhone: false,
      favorite: false
    });
  };

  // Get filter summary
  const getFilterSummary = () => {
    const activeFilters = [];
    if (contactFilters.source.length > 0) activeFilters.push(`Source: ${contactFilters.source.join(', ')}`);
    if (contactFilters.hasEmail) activeFilters.push('Has Email');
    if (contactFilters.hasPhone) activeFilters.push('Has Phone');
    if (contactFilters.favorite) activeFilters.push('Favorites');
    return activeFilters;
  };

  // Shared creation used by both "Create Game" and "Create & invite on WhatsApp".
  const createGameNow = async () => {
    if (!courseName.trim() || !name.trim() || !scheduledDate || isNaN(parseSAST(scheduledDate).getTime())) throw new Error('Choose a course and tee time first.');
    if (!Number.isFinite(betAmount) || betAmount < 50 || betAmount > 5000) throw new Error('Choose a stake between R50 and R5,000.');
    // Registered users are invited by id; imported contacts and anything
    // typed into the manual box are invited by email.
    const playerIds = selectedFriends
      .filter((f) => typeof f.userId === 'number')
      .map((f) => f.userId as number);
    const selectedEmails = selectedFriends
      .filter((f) => !f.userId && f.email)
      .map((f) => f.email as string);
    const manualEmails = playerEmails.split(',').map((e) => e.trim()).filter(Boolean);

    const gameData = {
      name,
      course_name: courseName,
      scheduled_date: scheduledDate,
      scoring_format: scoringFormat,
      holes,
      player_emails: Array.from(new Set([...selectedEmails, ...manualEmails])),
      player_ids: playerIds,
    };

    const newGame = await gamesApi.createGame(gameData);
    trackEvent('create_game', { holes, scoring_format: scoringFormat });

    // Create the winner-takes-all bet with the chosen stake (Rands -> cents)
    // and stake the creator immediately so the pool is never empty.
    if (betAmount && betAmount > 0) {
      try {
        const newBet = await betsApi.createBet({
          game_id: newGame.id,
          bet_type: 'winner_takes_all',
          amount_per_player: Math.round(betAmount * 100),
        });
        await betsApi.joinBet(newBet.id);
      } catch {
        // Non-fatal: the game is created; the bet can be set up in the game.
      }
    }
    return newGame;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const newGame = await createGameNow();
      navigate(`/game/${newGame.id}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to create game');
    } finally {
      setIsLoading(false);
    }
  };

  // Create the game, then hand off to WhatsApp with the shareable join link
  // (including the course). Opens the WhatsApp tab within the click gesture so
  // mobile browsers don't block the popup.
  const handleCreateAndInvite = async () => {
    if (!name.trim() || !courseName.trim() || !scheduledDate) {
      setError('Add a game name, course and date first.');
      return;
    }
    setError('');
    setIsLoading(true);
    const win = window.open('', '_blank');
    try {
      const newGame = await createGameNow();
      const code = (newGame as any).invite_code;
      const when = formatSAST(scheduledDate, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) + ' SAST';
      const link = code
        ? `${window.location.origin}/join/${code}`
        : `${window.location.origin}/game/${newGame.id}`;
      const text = `⛳ Join my FairPlay round "${newGame.name}" at ${courseName} on ${when}.\nTap to join: ${link}`;
      const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
      if (win) win.location.href = wa; else window.open(wa, '_blank');
      navigate(`/game/${newGame.id}`);
    } catch (err: any) {
      if (win) win.close();
      setError(err.response?.data?.detail || err.message || 'Failed to create game');
    } finally {
      setIsLoading(false);
    }
  };


  return <Layout><div className="quick-flow"><header className="quick-heading"><button type="button" onClick={()=>navigate('/')} aria-label="Back to home"><ArrowLeft/></button><div><p>LESS ADMIN. MORE GOLF.</p><h1>Get a round going.</h1></div><GolfIcon name="flag"/></header>
<form onSubmit={handleSubmit}>
          {/* Enhanced Course Selection */}
          <div className="bg-[#fbfaf6] rounded-2xl p-6 shadow-sm border border-[#e2dccf]">
            <label className="block text-sm font-bold text-green-900 mb-4">
              Course
            </label>
            
            {/* Enhanced Course Search */}
            <div className="quick-course-search relative">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-green-600" />
                </div>
                <input
                  type="text"
                  value={courseQuery}
                  onChange={(e) => {
                    setCourseQuery(e.target.value);
                    searchCourses(e.target.value);
                  }}
                  onFocus={() => {
                    if (nearbyCourses.length === 0 && !courseQuery) {
                      searchNearby(userLocation ?? undefined);
                    }
                    setShowCourseSuggestions(true);
                  }}
                  className="w-full pl-12 pr-4 py-4 border-2 border-green-300 rounded-xl focus:ring-4 focus:ring-green-200 focus:border-green-600 transition-all duration-200 bg-white/90 backdrop-blur font-medium"
                  placeholder="Search or enter a course"
                />
              </div>
              
              {/* Search Near Me Button */}
              <button
                type="button"
                onClick={handleSearchNearMe}
                disabled={isCourseLoading}
                className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition-all duration-200 font-medium shadow-lg disabled:opacity-70"
                title="Find nearby courses"
                aria-label="Find nearby courses"
              >
                {isCourseLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Locating…</span>
                  </>
                ) : locationError ? (
                  <>
                    <X className="h-5 w-5" />
                    <span>GPS Unavailable</span>
                  </>
                ) : userLocation ? (
                  <>
                    <MapPin className="h-5 w-5" />
                    <span>Search Near Me</span>
                  </>
                ) : (
                  <>
                    <Navigation className="h-5 w-5" />
                    <span>Search Near Me</span>
                  </>
                )}
              </button>

              {/* Course Suggestions Dropdown */}
              {showCourseSuggestions && (
                <div className="absolute z-10 w-full mt-3 bg-white/95 backdrop-blur border-2 border-green-200 rounded-2xl shadow-2xl max-h-96 overflow-y-auto">
                  {/* Nearby Courses Header */}
                  {!courseQuery && nearbyCourses.length > 0 && (
                    <div className="px-4 py-4 bg-gradient-to-r from-green-100 to-emerald-100 border-b border-green-200">
                      <div className="flex items-center text-green-800 text-sm font-bold">
                        <MapPin className="h-4 w-4 mr-2" />
                        {userLocation ? 'Championship Courses Near You' : 'Featured Championship Venues'}
                      </div>
                    </div>
                  )}

                  {/* Use exactly what was typed — works for any course, listed or not */}
                  {courseQuery.trim() && (
                    <button
                      type="button"
                      onClick={useTypedCourse}
                      className="w-full px-4 py-3 flex items-center space-x-3 text-left border-b border-green-100 hover:bg-green-50 transition"
                    >
                      <div className="bg-green-600 text-white rounded-lg p-2">
                        <MapPin className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-semibold text-green-900">Use “{courseQuery.trim()}”</div>
                        <div className="text-xs text-green-700">Play at this course even if it's not listed</div>
                      </div>
                    </button>
                  )}

                  {/* Course List */}
                  <div className="py-2">
                    {(courseQuery ? courseSuggestions : nearbyCourses).map((course: Course) => (
                      <button
                        key={course.id}
                        type="button"
                        onClick={() => handleCourseSelect(course)}
                        className="w-full px-4 py-4 hover:bg-gradient-to-r hover:from-green-50 hover:to-emerald-50 transition-all duration-200 flex items-start space-x-4 text-left border-b border-green-100 last:border-b-0"
                      >
                        <div className="flex-1">
                          <div className="font-bold text-green-900 text-lg">{course.name}</div>
                          <div className="text-sm text-green-700 flex items-center mt-1 font-medium">
                            <MapPin className="h-3 w-3 mr-1" />
                            {course.city}, {course.province}
                          </div>
                          <div className="flex items-center space-x-4 mt-3 text-xs">
                            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                              {course.holes} Holes
                            </div>
                            <div className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full font-semibold">
                              Par {course.par}
                            </div>
                            {course.distance_km && (
                              <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-semibold">
                                {course.distance_km} km
                              </div>
                            )}
                            {course.green_fee_weekday && (
                              <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-semibold">
                                R{course.green_fee_weekday}
                              </div>
                            )}
                            {course.is_verified && (
                              <div className="bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full font-semibold flex items-center">
                                <Star className="h-3 w-3 mr-1 fill-current" />
                                Verified
                              </div>
                            )}
                          </div>
                          <div className="flex space-x-2 mt-2">
                            {course.has_driving_range && <span className="text-xs bg-gray-100 px-2 py-1 rounded">🏌️ Range</span>}
                            {course.has_restaurant && <span className="text-xs bg-gray-100 px-2 py-1 rounded">🍽️ Dining</span>}
                            {course.has_carts && <span className="text-xs bg-gray-100 px-2 py-1 rounded">🚗 Carts</span>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Loading State */}
                  {isCourseLoading && (
                    <div className="px-4 py-8 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Searching...</span>
                    </div>
                  )}

                  {/* No Results */}
                  {!isCourseLoading && (courseQuery ? courseSuggestions : nearbyCourses).length === 0 && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No courses found</p>
                      <p className="text-xs mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Course Details */}
            {selectedCourse && (
              <div className="mt-6 p-6 bg-gradient-to-br from-green-100 to-emerald-100 border-2 border-green-300 rounded-2xl shadow-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-3">
                      <Trophy className="h-6 w-6 text-yellow-600 mr-3" />
                      <h4 className="font-bold text-green-900 text-xl">{selectedCourse.name}</h4>
                    </div>
                    <p className="text-sm text-green-700 font-medium mb-4">{selectedCourse.address}</p>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-white/60 backdrop-blur rounded-lg p-3">
                        <div className="text-xs text-green-600 font-semibold">COURSE DETAILS</div>
                        <div className="text-sm text-green-800 font-bold mt-1">
                          {selectedCourse.holes} Holes • Par {selectedCourse.par}
                        </div>
                        {selectedCourse.length_meters && (
                          <div className="text-xs text-green-700 mt-1">{selectedCourse.length_meters}m</div>
                        )}
                      </div>
                      <div className="bg-white/60 backdrop-blur rounded-lg p-3">
                        <div className="text-xs text-green-600 font-semibold">CHAMPIONSHIP RATING</div>
                        <div className="text-sm text-green-800 font-bold mt-1">
                          {selectedCourse.course_rating || 'TBD'}
                        </div>
                        <div className="text-xs text-green-700 mt-1">Slope {selectedCourse.slope_rating || 'TBD'}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedCourse.has_driving_range && <span className="text-xs bg-green-200 text-green-800 px-3 py-2 rounded-full font-semibold">🏌️ Driving Range</span>}
                      {selectedCourse.has_pro_shop && <span className="text-xs bg-green-200 text-green-800 px-3 py-2 rounded-full font-semibold">🏪 Pro Shop</span>}
                      {selectedCourse.has_restaurant && <span className="text-xs bg-green-200 text-green-800 px-3 py-2 rounded-full font-semibold">🍽️ Fine Dining</span>}
                      {selectedCourse.has_carts && <span className="text-xs bg-green-200 text-green-800 px-3 py-2 rounded-full font-semibold">🚗 Golf Carts</span>}
                      {selectedCourse.is_verified && <span className="text-xs bg-yellow-200 text-yellow-800 px-3 py-2 rounded-full font-semibold">⭐ GolfRSA Verified</span>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCourse(null);
                      setCourseName('');
                    }}
                    className="ml-4 p-2 text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full transition-all duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Quick Course Buttons */}
            <div className="flex flex-wrap gap-2 mt-3">
              {recentCourses.slice(0, 3).map(course => (
                <button
                  key={course}
                  type="button"
                  onClick={() => setCourseName(course)}
                  className="px-3 py-1 text-xs bg-gray-100 text-gray-600 rounded-full hover:bg-emerald-100 hover:text-emerald-700 transition"
                >
                  {course.split(' ')[0]}
                </button>
              ))}
            </div>
            
            {/* Location Error */}
            {locationError && (
              <div className="mt-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                <MapPin className="h-3 w-3 inline mr-1" />
                {locationError}
              </div>
            )}
            

          </div>


<section className="quick-section"><div className="quick-section-label"><Clock size={18}/><label htmlFor="tee-time">Tee time</label><small>SAST · UTC+2</small></div>
<div className="quick-date-chips">{(['now','today','tomorrow','weekend'] as const).map(day=><button type="button" key={day} onClick={()=>setScheduledDate(sastTeeTime(day))}>{day === 'now' ? 'Now' : day.charAt(0).toUpperCase()+day.slice(1)}</button>)}</div>
<input id="tee-time" aria-label="Tee time in South African Standard Time" type="datetime-local" value={scheduledDate} onChange={e=>setScheduledDate(e.target.value)} required/>
</section>
<section className="quick-section"><div className="quick-section-label"><GolfIcon name="wallet"/><span>Friendly stake</span><small>per player</small></div><div className="quick-stakes">{[50,100,200,500].map(amount=><button type="button" key={amount} aria-pressed={!customBet && betAmount === amount} onClick={()=>{setBetAmount(amount);setCustomBet('');}}>R{amount}</button>)}</div>
<details className="quick-custom"><summary>Choose another amount</summary><label htmlFor="custom-stake">Stake in rand (R50–R5,000)</label><input id="custom-stake" type="number" min="50" max="5000" inputMode="numeric" value={customBet} onChange={e=>{setCustomBet(e.target.value);setBetAmount(e.target.value ? Number(e.target.value) : 50);}}/></details></section>

<details className="quick-options"><summary><GolfIcon name="score"/><span>Round settings<small>{holes} holes · {scoringFormat.replace('_',' ')} · {name}</small></span><span className="quick-edit">Edit</span></summary><div className="quick-options-body">
          {/* Scoring Format */}
          <div ref={scoringRef} className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 scroll-mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              <Trophy className="inline h-4 w-4 mr-1" />
              How are we keeping score? 🏆
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'stableford', label: 'Stableford', desc: 'Points per hole' },
                { value: 'stroke_play', label: 'Stroke Play', desc: 'Total strokes' },
                { value: 'match_play', label: 'Match Play', desc: 'Hole by hole' }
              ].map(format => (
                <button
                  key={format.value}
                  type="button"
                  onClick={() => setScoringFormat(format.value)}
                  className={`px-4 py-3 rounded-xl font-medium transition ${
                    scoringFormat === format.value
                      ? 'bg-emerald-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <div className="text-sm">{format.label}</div>
                  <div className="text-xs opacity-75">{format.desc}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-2">Bets currently settle on <span className="font-medium text-gray-500">net (handicap-adjusted) strokes</span> whichever format you pick.</p>
          </div>

          {/* Round length */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              <Flag className="inline h-4 w-4 mr-1" />
              How many holes?
            </label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { value: 9, label: '9 Holes', desc: 'Quick nine' },
                { value: 18, label: '18 Holes', desc: 'The full round' },
              ].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setHoles(opt.value)}
                  className={`px-4 py-3 rounded-xl font-medium transition ${
                    holes === opt.value ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <div className="text-sm font-bold">{opt.label}</div>
                  <div className="text-xs opacity-75">{opt.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Game Name */}
          <div className="bg-[#fbfaf6] rounded-2xl p-6 shadow-sm border border-[#e2dccf]">
            <label className="block text-sm font-bold text-green-900 mb-3 flex items-center">
              <Trophy className="h-5 w-5 mr-2 text-yellow-600" />
              Game Name
            </label>
            <div className="flex gap-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 px-4 py-4 rounded-xl border-2 border-green-300 focus:border-green-600 focus:ring-4 focus:ring-green-200 transition-all duration-200 bg-white/80 backdrop-blur font-medium"
                placeholder="The Masters Challenge"
                required
              />
              <button
                type="button"
                onClick={randomizeGameName}
                className="px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all duration-200 font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                Random name
              </button>
            </div>
            <p className="text-xs text-green-700 mt-3 font-medium italic">Give the WhatsApp group something to fear.</p>
          </div>


</div></details><details className="quick-options"><summary><GolfIcon name="mates"/><span>Invite your mates<small>{selectedFriends.length ? selectedFriends.length + ' selected' : 'Optional — share the invite after creating'}</small></span><span className="quick-edit">Add</span></summary><div className="quick-options-body">
          {/* Enhanced Friend Search */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              <Users className="inline h-4 w-4 mr-1" />
              Invite players
            </label>
            
            {/* Friend Search Input */}
            <div className="relative mb-3">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={friendQuery}
                  onChange={(e) => {
                    setFriendQuery(e.target.value);
                    searchFriends(e.target.value);
                  }}
                  onFocus={() => setShowFriendSuggestions(true)}
                  className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
                  placeholder="Search players by name or email..."
                />
                
                {/* Contact Upload Button */}
                <button
                  type="button"
                  onClick={() => setShowContactUpload(true)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  title="Import contacts"
                >
                  <UserPlus className="h-5 w-5 text-gray-400 hover:text-emerald-500 transition" />
                </button>
              </div>

              {/* Friend Suggestions Dropdown */}
              {showFriendSuggestions && (
                <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-80 overflow-y-auto">
                  {/* Friend List */}
                  <div className="py-2">
                    {friendSuggestions.map((friend: any) => (
                      <button
                        key={friend.id}
                        type="button"
                        onClick={() => handleFriendSelect(friend)}
                        className="w-full px-4 py-3 hover:bg-gray-50 transition flex items-start space-x-3 text-left"
                      >
                        <Avatar url={(friend as any).avatar_url} name={friend.name} size="w-10 h-10" bg="bg-emerald-100" text="text-emerald-700 font-medium text-sm" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 truncate">
                            {friend.name} {friend.surname}
                          </div>
                          <div className="text-sm text-gray-500 truncate">{friend.email || friend.homeClub || (typeof friend.handicap === 'number' ? `Handicap Index ${formatHandicapIndex(friend.handicap)}` : 'Golfer')}</div>
                          <div className="flex items-center space-x-4 mt-1 text-xs">
                            {friend.phone && (
                              <div className="flex items-center text-gray-500">
                                <Phone className="h-3 w-3 mr-1" />
                                {friend.phone}
                              </div>
                            )}
                            {typeof friend.handicap === 'number' && (
                              <div className="text-brand-700">
                                Handicap Index: {formatHandicapIndex(friend.handicap)}
                              </div>
                            )}
                            {friend.homeClub && (
                              <div className="text-gray-500">
                                {friend.homeClub}
                              </div>
                            )}
                          </div>
                        </div>
                        <UserPlus className="h-5 w-5 text-emerald-500" />
                      </button>
                    ))}
                  </div>

                  {/* Loading State */}
                  {isFriendLoading && (
                    <div className="px-4 py-8 flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-gray-500">Searching...</span>
                    </div>
                  )}

                  {/* No Results */}
                  {!isFriendLoading && friendSuggestions.length === 0 && (
                    <div className="px-4 py-8 text-center text-gray-500">
                      <Search className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No players found</p>
                      <p className="text-xs mt-1">They may not have joined yet — invite by email below</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected Friends */}
            {selectedFriends.length > 0 && (
              <div className="bg-emerald-50 rounded-xl p-4 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-emerald-900">Selected Players ({selectedFriends.length})</h4>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedFriends([]);
                      setPlayerEmails('');
                    }}
                    className="text-emerald-600 hover:text-emerald-700 text-sm"
                  >
                    Clear all
                  </button>
                </div>
                <div className="space-y-2">
                  {selectedFriends.map((friend: any) => (
                    <div key={friend.id} className="flex items-center justify-between bg-white rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <Avatar url={(friend as any).avatar_url} name={friend.name} size="w-8 h-8" bg="bg-emerald-100" text="text-emerald-700 font-medium text-xs" />
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 text-sm truncate">
                            {friend.name} {friend.surname}
                          </div>
                          <div className="text-xs text-gray-500 truncate">{friend.email || friend.homeClub || 'Registered golfer'}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveFriend(friend.id)}
                        aria-label={`Remove ${friend.name}`}
                        className="p-2 shrink-0 rounded-full text-red-500 hover:text-red-700 hover:bg-red-50 transition"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Traditional Email Input */}
            <textarea
              value={playerEmails}
              onChange={(e) => setPlayerEmails(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition"
              placeholder="Or enter emails manually: friend@email.com, buddy@email.com..."
              rows={2}
            />
            <p className="text-xs text-gray-500 mt-1">
              Search friends • Import contacts • Enter emails manually • Or just show up and surprise them! 😈
            </p>
          </div>


</div></details>
          {/* Contact Upload Modal */}
          {showContactUpload && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              {/* Backdrop */}
              <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setShowContactUpload(false)}
              />

              {/* Modal */}
              <div className="relative w-full max-w-md max-h-[90dvh] overflow-y-auto bg-white rounded-2xl p-6">
                <button
                  onClick={() => setShowContactUpload(false)}
                  className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
                
                <h3 className="text-lg font-bold text-gray-900 mb-4">Import Contacts</h3>
                
                <div className="space-y-4">
                  {/* Phone Contacts */}
                  {contactPermission === 'granted' ? (
                    <button
                      onClick={loadContacts}
                      disabled={isFriendLoading}
                      className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition disabled:opacity-50"
                    >
                      <Phone className="h-5 w-5" />
                      <span>{isFriendLoading ? 'Loading Contacts...' : 'Sync Phone Contacts'}</span>
                    </button>
                  ) : contactPermission === 'denied' ? (
                    <div className="text-center">
                      <p className="text-sm text-red-600 mb-2">Contact access denied</p>
                      <p className="text-xs text-gray-500">Please enable contacts in your device settings</p>
                    </div>
                  ) : contactPermission === 'not_available' ? (
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Contacts not available in this browser</p>
                      <p className="text-xs text-gray-500 mb-3">
                        {getBrowserInfo().platform}: {getBrowserInfo().recommendation}
                      </p>
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                        <p className="text-xs text-amber-800 font-medium mb-2">Why contact sync isn't available:</p>
                        <ul className="text-xs text-amber-700 text-left space-y-1">
                          <li>• Most browsers don't support contact access for privacy</li>
                          <li>• Only mobile Chrome/Edge have limited support</li>
                          <li>• Desktop browsers have no contact API</li>
                        </ul>
                      </div>
                      <p className="text-xs text-green-600 font-medium">📁 Use file upload below instead!</p>
                    </div>
                  ) : (
                    <button
                      onClick={requestContactPermission}
                      disabled={isContactLoading}
                      className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white py-3 rounded-xl hover:bg-green-700 transition disabled:opacity-50"
                    >
                      <Phone className="h-5 w-5" />
                      <span>{isContactLoading ? 'Requesting Permission...' : 'Request Contact Access'}</span>
                    </button>
                  )}
                  
                  {/* File Upload */}
                  <div className="text-center">
                    <div className="border-2 border-dashed border-green-300 rounded-xl p-6">
                      <Upload className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-sm text-gray-700 mb-2">Upload contact file</p>
                      <p className="text-xs text-gray-500 mb-3">CSV, VCF, or JSON files</p>
                      
                      <div className="space-y-3">
                        <input
                          type="file"
                          accept=".csv,.vcf,.json"
                          onChange={handleFileUpload}
                          className="hidden"
                          id="contact-file-upload"
                        />
                        <label
                          htmlFor="contact-file-upload"
                          className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition cursor-pointer"
                        >
                          Choose File
                        </label>
                        
                        <div className="text-xs text-gray-600 space-y-2">
                          <div className="font-medium text-gray-700">📁 Download templates:</div>
                          <div className="space-y-1">
                            <div>
                              <a
                                href="/contacts-template.csv"
                                download="fairplay-basic-template.csv"
                                className="text-green-600 hover:text-green-700 underline"
                              >
                                Basic Template (CSV)
                              </a>
                              <span className="text-gray-500 ml-1">• Simple format</span>
                            </div>
                            <div>
                              <a
                                href="/contacts-enhanced-template.csv"
                                download="fairplay-enhanced-template.csv"
                                className="text-green-600 hover:text-green-700 underline"
                              >
                                Enhanced Template (CSV)
                              </a>
                              <span className="text-gray-500 ml-1">• With groups & favorites</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500 border-t pt-2">
                          <div className="font-medium mb-1">Supported formats:</div>
                          <div className="space-y-1">
                            <div>• <strong>CSV</strong>: Comma-separated values</div>
                            <div>• <strong>VCF</strong>: vCard contact files</div>
                            <div>• <strong>JSON</strong>: Custom contact objects</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 text-center">
                    We'll only access names and contact information
                  </p>
                </div>
              </div>
            </div>
          )}


{error && <div role="alert" className="quick-error">{error}</div>}
<div className="quick-submit"><div className="quick-summary"><strong>{courseName || 'Choose your course'}</strong><span>{formatSAST(scheduledDate)} SAST · R{betAmount || 0} each</span></div><button type="submit" disabled={isLoading || !courseName.trim() || !scheduledDate}>{isLoading ? 'Creating your round…' : 'Create round'}<GolfIcon name="flag"/></button></div>
<button type="button" className="quick-share" onClick={handleCreateAndInvite} disabled={isLoading || !courseName.trim() || !scheduledDate}><Share2 size={16}/>Or create & invite on WhatsApp</button>
</form></div></Layout>;
};
export default QuickGame;
